// biome-ignore-all lint/suspicious/noArrayIndexKey: historic
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import {
  EM_SIGN_IMAGES_MARGIN_RIGHT,
  EM_SIG_CONTACT_CELL_PADDING,
  EM_SIG_CONTACT_ICONS_PADDING,
  EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE,
  EM_SIG_FONT_COLOR,
  EM_SIG_FONT_SIZE_BASE,
  EM_SIG_FONT_SIZE_EXTENSION,
  EM_SIG_FONT_SIZE_LABEL,
  EM_SIG_FONT_SIZE_LABEL_WITHOUT_ICON,
  EM_SIG_FONT_SIZE_PRIMARY,
  EM_SIG_FONT_SIZE_SECONDARY,
  EM_SIG_IMAGES_SIZES_DICT,
  EM_SIG_MAXIMUM_FULL_WIDTH_CONTACT_FIELD_LENGTH,
  EM_SIG_MAXIMUM_FULL_WIDTH_CONTACT_FIELD_LENGTH_WITHOUT_ICON,
  EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH,
  EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH_WITHOUT_ICON,
  EM_SIG_PERSONAL_DETAILS_MAX_WIDTH,
  EM_SIG_SECONDARY_FONT_COLOR,
  EM_SIG_SOCIAL_ICON_PADDING,
  EM_SIG_SOCIAL_ICON_SIZES_DICT,
  EmailSignatureContactDetailsFieldWidth,
  MAIN_SECTION_WITHOUT_IMAGE_WIDTH,
  SAVE_CONTACT_BUTTON_WIDTH,
} from '@components/EmailSignaturev2/consts';
import {
  type EmailSignatureContactDetailsField,
  type EmailSignatureContactDetailsFieldId,
  type EmailSignatureDesign,
  type EmailSignaturePersonalDetailsFields,
  EmailSignatureSizes,
  type EmailSignatureSocialLink,
  type EmailSignatureV2,
  type EmailSignaturesImages,
} from '@components/EmailSignaturev2/types';
import { NEXT_PUBLIC_BLINQ_FORWARD_BASE_URL } from '@env';
import { getName, getURIFromURLFieldValue } from '@modules/card-helpers';
import {
  formatEmailSignaturePhoneExtension,
  getActualContactDetailValueToDisplay,
  getActualLinkForContactDetailsField,
  getBorderRadiusForSocialLinkOutline,
  getSocialLinkIconColor,
  isObjectActuallyEmpty,
} from '@modules/email-signatures-helpers';
import Emitter from '@modules/event-emitter';
import { buildUrl } from '@modules/url-helpers';
import { capitalize } from 'es-toolkit';
// es-toolkit/compat: lodash chunk returns [] for size <= 0 (es-toolkit main throws); lodash isEmpty treats 0/true/null as empty
import { chunk, isEmpty } from 'es-toolkit/compat';
import { type FC, useEffect, useMemo, useState } from 'react';
import type { CardUrlType } from 'types';

import styles from './EmailSignatureCompactMainSection.module.scss';
import { SaveContactButton } from './EmailSignatureCompactSaveContactButton';

type EmailSignatureCompactMainSectionProps = {
  emailSignature: EmailSignatureV2;
  disableAllSpecialEffects: boolean;
  disableAnchorLink: boolean;
  /** Set this to true when you are copying the email signature for use in an email client.
   * Set to false when using this component in the dashboard.
   */
  isForEmailClient?: boolean;
};

const EmailSignatureCompactMainSection: FC<
  React.PropsWithChildren<EmailSignatureCompactMainSectionProps>
> = ({ emailSignature, disableAllSpecialEffects, disableAnchorLink, isForEmailClient = false }) => {
  const [contactDetailsFieldIdWithOutline, setContactDetailsFieldIdWithOutline] =
    useState<EmailSignatureContactDetailsFieldId | null>(null);
  const socialLinksIndicesRows = useMemo(() => {
    const actualSocialLinksOrder = emailSignature.socialLinksOrder.filter(
      (socialLinkId) => emailSignature.socialLinks[socialLinkId].value,
    );
    return chunk(
      actualSocialLinksOrder,
      emailSignature.saveContact.displaySaveContact && emailSignature.saveContact ? 6 : 9,
    );
  }, [emailSignature.saveContact, emailSignature.socialLinks, emailSignature.socialLinksOrder]);

  const socialLinksRows = useMemo(
    () =>
      socialLinksIndicesRows.map((socialLinksIndicesRow) =>
        socialLinksIndicesRow.map(
          (socialLinkFieldId) => emailSignature.socialLinks[socialLinkFieldId],
        ),
      ),
    [emailSignature.socialLinks, socialLinksIndicesRows],
  );

  const contactRows = useMemo(() => {
    const contactDetailsRows: EmailSignatureContactDetailsField[][] = [];

    emailSignature.contactDetailsOrder.forEach((contactDetailFieldId, index) => {
      const contactDetail = emailSignature.contactDetails[contactDetailFieldId];
      if (contactDetail.fieldWidth === EmailSignatureContactDetailsFieldWidth.FULL_WIDTH) {
        contactDetailsRows.push([contactDetail]);
        index !== emailSignature.contactDetailsOrder.length - 1 && contactDetailsRows.push([]);
      } else {
        const indexOfLastEl = contactDetailsRows.length - 1;
        if (contactDetailsRows[indexOfLastEl]?.length === 2 || contactDetailsRows.length === 0) {
          contactDetailsRows.push([contactDetail]);
        } else if (contactDetailsRows[indexOfLastEl]?.length < 2)
          contactDetailsRows[indexOfLastEl].push(contactDetail);
      }
    });
    return contactDetailsRows;
  }, [emailSignature.contactDetailsOrder, emailSignature.contactDetails]);

  const showSocialSeparatorRow = useMemo(
    () =>
      !(
        isObjectActuallyEmpty(emailSignature.personalDetails) &&
        isEmpty(emailSignature.contactDetails)
      ),
    [emailSignature.contactDetails, emailSignature.personalDetails],
  );

  const showContactFieldsSeparatorRow = useMemo(
    () => !isObjectActuallyEmpty(emailSignature.personalDetails),
    [emailSignature.personalDetails],
  );

  useEffect(() => {
    function handleTurnOnFieldWidthOutline(fieldId: EmailSignatureContactDetailsFieldId) {
      setContactDetailsFieldIdWithOutline(fieldId);
    }
    Emitter.on('TURN_ON_FIELD_WIDTH_OUTLINE', handleTurnOnFieldWidthOutline);

    function handleTurnOffFieldWidthOutline(fieldId: EmailSignatureContactDetailsFieldId) {
      setContactDetailsFieldIdWithOutline((prevFieldIdWithOutline) => {
        if (prevFieldIdWithOutline === fieldId) {
          return null;
        } else {
          return prevFieldIdWithOutline;
        }
      });
    }
    Emitter.on('TURN_OFF_FIELD_WIDTH_OUTLINE', handleTurnOffFieldWidthOutline);

    return () => {
      Emitter.off('TURN_ON_FIELD_WIDTH_OUTLINE', handleTurnOnFieldWidthOutline);
      Emitter.off('TURN_OFF_FIELD_WIDTH_OUTLINE', handleTurnOffFieldWidthOutline);
    };
  }, []);

  const nonEmptyImages = useMemo(
    () => emailSignature.images.filter((image) => image.imageUrl),
    [emailSignature.images],
  );

  return (
    <table
      style={{
        borderCollapse: 'collapse',
        lineHeight: 1,
        fontSize: `${EM_SIG_FONT_SIZE_BASE}px`,
      }}
      cellSpacing="0"
      cellPadding="0"
    >
      <tbody>
        <tr>
          {nonEmptyImages.length > 0 && emailSignature.images[0].id !== 'im_dummy' && (
            <CompactImagesCell
              images={emailSignature.images}
              disableAllSpecialEffects={disableAllSpecialEffects}
            />
          )}
          {emailSignature.images.length > 0 && emailSignature.images[0].id === 'im_dummy' && (
            <CompactDummyImagesCell disableAllSpecialEffects={disableAllSpecialEffects} />
          )}

          <td valign="top" style={{ verticalAlign: 'top' }}>
            <table style={{ borderCollapse: 'collapse' }} cellSpacing="0" cellPadding="0">
              <tbody>
                <tr>
                  <td
                    role={disableAllSpecialEffects ? undefined : 'button'}
                    tabIndex={disableAllSpecialEffects ? undefined : 0}
                    className={
                      disableAllSpecialEffects ? undefined : styles['interactive-personal-details']
                    }
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        Emitter.emit('SCROLL_TO_PERSONAL_DETAILS');
                      } else if (event.key === ' ') {
                        event.preventDefault();
                      }
                    }}
                    onClick={() => Emitter.emit('SCROLL_TO_PERSONAL_DETAILS')}
                  >
                    <table
                      style={{
                        borderCollapse: 'collapse',
                      }}
                      cellSpacing="0"
                      cellPadding="0"
                    >
                      <tbody>
                        <tr>
                          {
                            <CompactPersonalDetailsRows
                              personalDetails={emailSignature.personalDetails}
                              emailSignatureDesign={emailSignature.design}
                            />
                          }
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>

                {contactRows.length > 0 && (
                  <>
                    {showContactFieldsSeparatorRow && (
                      <tr>
                        <CompactSeparator
                          emailSignatureDesign={emailSignature.design}
                          isBelowPersonalDetails
                        />
                      </tr>
                    )}
                    <tr>
                      <td
                        style={{
                          lineHeight: 0,
                        }}
                      >
                        <table
                          style={{ borderCollapse: 'collapse' }}
                          cellSpacing="0"
                          cellPadding="0"
                        >
                          <tbody>
                            {contactRows.map((contactRow, index) => (
                              <CompactContactsRow
                                disableAnchorLink={disableAnchorLink}
                                disableAllSpecialEffects={disableAllSpecialEffects}
                                emailSignatureDesign={emailSignature.design}
                                isLastRow={index === contactRows.length - 1}
                                key={index}
                                cells={contactRow}
                                contactDetailsFieldIdWithOutline={contactDetailsFieldIdWithOutline}
                              />
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </>
                )}

                {(socialLinksIndicesRows.length > 0 ||
                  emailSignature.saveContact.displaySaveContact) && (
                  <>
                    {showSocialSeparatorRow && (
                      <tr>
                        <CompactSeparator
                          emailSignatureDesign={emailSignature.design}
                          isBelowPersonalDetails={false}
                        />
                      </tr>
                    )}
                    <tr>
                      <td
                        style={{
                          borderCollapse: 'collapse',
                        }}
                      >
                        <table
                          style={{
                            borderCollapse: 'collapse',
                          }}
                          cellSpacing="0"
                          cellPadding="0"
                        >
                          <tbody>
                            <tr>
                              <td
                                style={{
                                  width: emailSignature.saveContact.displaySaveContact
                                    ? `${MAIN_SECTION_WITHOUT_IMAGE_WIDTH - SAVE_CONTACT_BUTTON_WIDTH}px`
                                    : `${MAIN_SECTION_WITHOUT_IMAGE_WIDTH}px`,
                                }}
                                width={
                                  emailSignature.saveContact.displaySaveContact
                                    ? MAIN_SECTION_WITHOUT_IMAGE_WIDTH - SAVE_CONTACT_BUTTON_WIDTH
                                    : MAIN_SECTION_WITHOUT_IMAGE_WIDTH
                                }
                              >
                                <table cellSpacing="0" cellPadding="0">
                                  <tbody>
                                    {socialLinksRows.map((socialLinksRow, index) => (
                                      <CompactSocialRow
                                        disableAnchorLink={disableAnchorLink}
                                        disableAllSpecialEffects={disableAllSpecialEffects}
                                        isLastRow={index === socialLinksRows.length - 1}
                                        key={index}
                                        socialLinks={socialLinksRow}
                                        emailSignatureDesign={emailSignature.design}
                                      />
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                              {emailSignature.saveContact.displaySaveContact && (
                                <SaveContactButton
                                  emailSignature={emailSignature}
                                  disableAnchorLink={disableAnchorLink}
                                  disableAllSpecialEffects={disableAllSpecialEffects}
                                  buttonType={isForEmailClient ? 'image' : 'html'}
                                />
                              )}
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

type CompactImagesCellProps = {
  images: EmailSignaturesImages;
  disableAllSpecialEffects: boolean;
};

const CompactImagesCell: FC<React.PropsWithChildren<CompactImagesCellProps>> = ({
  images,
  disableAllSpecialEffects,
}) => {
  const primaryImage = useMemo(() => {
    const indexOfPrimaryImage = images.findIndex((image) => image.isPrimary);
    if (indexOfPrimaryImage === -1) return undefined;

    const primaryImage = images[indexOfPrimaryImage];
    return primaryImage;
  }, [images]);

  const secondaryImage = useMemo(() => {
    const indexOfSecondaryImage = images.findIndex((image) => !image.isPrimary);
    if (indexOfSecondaryImage === -1) return undefined;

    const secondaryImage = images[indexOfSecondaryImage];
    return secondaryImage;
  }, [images]);

  return (
    <td
      style={{
        verticalAlign: 'top',
        paddingRight: `${EM_SIGN_IMAGES_MARGIN_RIGHT}px`,
      }}
    >
      <table style={{ borderCollapse: 'collapse' }} cellSpacing="0" cellPadding="0">
        <tbody className={disableAllSpecialEffects ? undefined : styles['dummy-images-body']}>
          {primaryImage?.imageUrl && (
            <tr>
              <td
                className={
                  disableAllSpecialEffects ? undefined : styles['interactive-preview-image']
                }
                style={{ textAlign: 'center', paddingBottom: '15px' }}
              >
                <a
                  tabIndex={disableAllSpecialEffects ? undefined : 0}
                  onClick={(event) => {
                    event.preventDefault();
                    Emitter.emit('SCROLL_TO_IMAGE', primaryImage?.id);
                  }}
                  onKeyDown={(event: React.KeyboardEvent) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      Emitter.emit('SCROLL_TO_IMAGE', primaryImage?.id);
                    } else if (event.key === ' ') {
                      event.preventDefault();
                    }
                  }}
                  style={{ display: 'block' }}
                  href={
                    primaryImage?.externalLink || undefined // we have to set undefined here because if it is empty string, when we copy, the clipboard api with replace it with
                    // url of the actual Blinq dashboard website since it assumes empty string refers to relative path
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    alt={'Profile'}
                    style={{
                      verticalAlign: 'initial',
                    }}
                    src={primaryImage.imageUrl}
                    width={`${EM_SIG_IMAGES_SIZES_DICT[primaryImage?.size ?? EmailSignatureSizes.MEDIUM]}`}
                  />
                </a>
              </td>
            </tr>
          )}
          {secondaryImage?.imageUrl && (
            <tr>
              <td
                className={
                  disableAllSpecialEffects ? undefined : styles['interactive-preview-image']
                }
                style={{ textAlign: 'center' }}
              >
                <a
                  onClick={(event) => {
                    event.preventDefault();
                    Emitter.emit('SCROLL_TO_IMAGE', secondaryImage?.id);
                  }}
                  tabIndex={disableAllSpecialEffects ? undefined : 0}
                  onKeyDown={(event: React.KeyboardEvent) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      Emitter.emit('SCROLL_TO_IMAGE', secondaryImage?.id);
                    } else if (event.key === ' ') {
                      event.preventDefault();
                    }
                  }}
                  style={{ display: 'block' }}
                  href={
                    secondaryImage?.externalLink || undefined // we have to set undefined here because if it is empty string, when we copy, the clipboard api with replace it with
                    // url of the actual Blinq dashboard website since it assumes empty string refers to relative path
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    alt={'Profile'}
                    style={{
                      verticalAlign: 'initial',
                      borderRadius: '0px',
                    }}
                    src={secondaryImage.imageUrl}
                    width={`${EM_SIG_IMAGES_SIZES_DICT[secondaryImage?.size ?? EmailSignatureSizes.MEDIUM]}`}
                  />
                </a>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </td>
  );
};

const CompactDummyImagesCell: FC<
  React.PropsWithChildren<{ disableAllSpecialEffects: boolean }>
> = ({ disableAllSpecialEffects }) => {
  return (
    <td
      style={{
        verticalAlign: 'top',
        paddingRight: `${EM_SIGN_IMAGES_MARGIN_RIGHT}px`,
      }}
    >
      <table style={{ borderCollapse: 'collapse' }} cellSpacing="0" cellPadding="0">
        <tbody className={styles['dummy-images-body']}>
          <tr>
            <td style={{ textAlign: 'center', paddingBottom: '15px' }}>
              <div
                onKeyDown={(event: React.KeyboardEvent) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    Emitter.emit('SCROLL_TO_IMAGES_PANEL');
                  } else if (event.key === ' ') {
                    event.preventDefault();
                  }
                }}
                onClick={() => Emitter.emit('SCROLL_TO_IMAGES_PANEL')}
                role="button"
                tabIndex={0}
                data-disable-hover={disableAllSpecialEffects.toString()}
                className={styles['primary-image-dummy']}
              >
                Primary
              </div>
            </td>
          </tr>
          <tr>
            <td style={{ textAlign: 'center' }}>
              <div
                role="button"
                tabIndex={0}
                onKeyDown={(event: React.KeyboardEvent) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    Emitter.emit('SCROLL_TO_IMAGES_PANEL');
                  } else if (event.key === ' ') {
                    event.preventDefault();
                  }
                }}
                onClick={() => Emitter.emit('SCROLL_TO_IMAGES_PANEL')}
                data-disable-hover={disableAllSpecialEffects.toString()}
                className={styles['secondary-image-dummy']}
              >
                Secondary
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </td>
  );
};

type CompactPersonalDetailsRowsProps = {
  personalDetails: EmailSignaturePersonalDetailsFields;
  emailSignatureDesign: EmailSignatureDesign;
};

const CompactPersonalDetailsRows: FC<React.PropsWithChildren<CompactPersonalDetailsRowsProps>> = ({
  personalDetails,
  emailSignatureDesign,
}) => {
  const name = useMemo(
    () =>
      getName({
        firstName: personalDetails.firstName,
        lastName: personalDetails.lastName,
      }),
    [personalDetails.firstName, personalDetails.lastName],
  );

  const showFirstBreakLine = useMemo(
    () => name && personalDetails.jobTitle,
    [personalDetails.jobTitle, name],
  );

  const showSecondBreakLine = useMemo(
    () => (name || personalDetails.jobTitle) && personalDetails.company,
    [personalDetails.company, personalDetails.jobTitle, name],
  );

  const showThirdBreakLine = useMemo(
    () => (name || personalDetails.jobTitle || personalDetails.company) && personalDetails.headline,
    [personalDetails.company, personalDetails.jobTitle, name, personalDetails.headline],
  );

  return (
    <td
      style={{
        maxWidth: `${EM_SIG_PERSONAL_DETAILS_MAX_WIDTH}px`,
        breakWord: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: '1.5',
      }}
    >
      {name && (
        <span
          style={{
            fontWeight: 'bold',
            lineHeight: 1,
          }}
        >
          <span
            role="button"
            style={{
              fontFamily: emailSignatureDesign.font,
              color: `${EM_SIG_FONT_COLOR}`,
              fontSize: `${EM_SIG_FONT_SIZE_PRIMARY}px`,
            }}
          >
            {name}
          </span>
        </span>
      )}

      {/* p tag does not work nicely with email html because different email clients will display p tags differently
            so the only way to display paragraphs consistently and reliably across different email clients is to use span
            and br tags*/}
      {showFirstBreakLine && <br />}
      {personalDetails.jobTitle && (
        <>
          <span
            style={{
              fontFamily: emailSignatureDesign.font,
              fontSize: `${EM_SIG_FONT_SIZE_BASE}px`,
              fontWeight: 'medium',
              color: `${EM_SIG_FONT_COLOR}`,
              marginTop: '-4px',
            }}
          >
            {personalDetails.jobTitle}
          </span>
        </>
      )}

      {/* p tag does not work nicely with email html because different email clients will display p tags differently
            so the only way to display paragraphs consistently and reliably across different email clients is to use span
            and br tags*/}
      {showSecondBreakLine && <br />}
      {personalDetails.company && (
        <>
          <span
            style={{
              fontFamily: emailSignatureDesign.font,
              fontSize: `${EM_SIG_FONT_SIZE_BASE}px`,
              fontWeight: 'medium',
              color: `${EM_SIG_FONT_COLOR}`,
              marginTop: '-4px',
            }}
          >
            {personalDetails.company}
          </span>
        </>
      )}

      {/* p tag does not work nicely with email html because different email clients will display p tags differently
            so the only way to display paragraphs consistently and reliably across different email clients is to use span
            and br tags*/}
      {showThirdBreakLine && <br />}
      {personalDetails.headline && (
        <span
          style={{
            fontFamily: emailSignatureDesign.font,
            fontSize: `${EM_SIG_FONT_SIZE_SECONDARY}px`,
            fontWeight: 'lighter',
            color: `${EM_SIG_FONT_COLOR}`,
            letterSpacing: '0.2px',
          }}
        >
          {personalDetails.headline}
        </span>
      )}
    </td>
  );
};

type CompactSocialRowProps = {
  iAmDummy?: boolean;
  socialLinks: EmailSignatureSocialLink[];
  isLastRow: boolean;
  emailSignatureDesign: EmailSignatureDesign;
  disableAllSpecialEffects: boolean;
  disableAnchorLink: boolean;
};

const CompactSocialRow: FC<React.PropsWithChildren<CompactSocialRowProps>> = ({
  socialLinks,
  isLastRow,
  emailSignatureDesign,
  disableAllSpecialEffects,
  disableAnchorLink,
}) => {
  return (
    <>
      <tr>
        <td>
          <table
            style={{
              borderCollapse: 'collapse',
            }}
            cellSpacing="0"
            cellPadding="0"
          >
            <tbody>
              <tr>
                <td
                  style={{
                    borderCollapse: 'collapse',
                    paddingBottom: isLastRow ? undefined : '10px',
                  }}
                >
                  <table cellSpacing="0" cellPadding="0">
                    <tbody>
                      <tr>
                        {socialLinks.map((socialLink, index) => (
                          <CompactSocialRowCell
                            disableAnchorLink={disableAnchorLink}
                            emailSignatureDesign={emailSignatureDesign}
                            disableAllSpecialEffects={disableAllSpecialEffects}
                            key={index}
                            socialLink={socialLink}
                            size={emailSignatureDesign.socialLinksSize}
                          />
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </td>
      </tr>
    </>
  );
};

type CompactSocialRowCellProps = {
  socialLink: EmailSignatureSocialLink;
  size: EmailSignatureSizes;
  disableAllSpecialEffects: boolean;
  disableAnchorLink: boolean;
  emailSignatureDesign: EmailSignatureDesign;
};

const CompactSocialRowCell: FC<React.PropsWithChildren<CompactSocialRowCellProps>> = ({
  socialLink,
  size,
  disableAllSpecialEffects,
  emailSignatureDesign,
  disableAnchorLink,
}) => {
  const socialLinkValue = useMemo(() => {
    const url = getURIFromURLFieldValue(socialLink?.type as CardUrlType, socialLink?.value ?? '');

    if (url?.startsWith('http')) {
      // If the link is a website link, we will use the proxy URL to that destination
      return buildUrl(NEXT_PUBLIC_BLINQ_FORWARD_BASE_URL, socialLink.id);
    }
    return url;
  }, [socialLink.id, socialLink?.type, socialLink?.value]);

  const iconName = useMemo(() => {
    switch (socialLink.type) {
      case 'twitter':
        return 'X icon';
      default:
        return `${socialLink.type} icon`;
    }
  }, [socialLink.type]);

  return (
    <td
      style={{
        paddingRight: `${EM_SIG_SOCIAL_ICON_PADDING}px`,
        textAlign: 'center',
        paddingTop: 0,
        '--icon-color': socialLink?.type
          ? getSocialLinkIconColor(socialLink.type, emailSignatureDesign)
          : undefined,
        '--social-outline-border-radius': getBorderRadiusForSocialLinkOutline(emailSignatureDesign),
      }}
      align="left"
      role={disableAllSpecialEffects ? undefined : 'button'}
      tabIndex={disableAllSpecialEffects ? undefined : 0}
      className={disableAllSpecialEffects ? undefined : styles['interactive-social-link']}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          Emitter.emit('SCROLL_TO_SOCIAL_LINKS_SECTION');
        } else if (event.key === ' ') {
          event.preventDefault();
        }
      }}
      onClick={() => {
        Emitter.emit('SCROLL_TO_SOCIAL_LINKS_SECTION');
      }}
    >
      <a
        href={(!disableAnchorLink && socialLinkValue) || undefined} // we have to set undefined here because if it is empty string, when we copy, the clipboard api with replace it with
        // url of the actual Blinq dashboard website url
        target="_blank"
        onClick={(event) => {
          event.preventDefault();
        }}
        rel="noreferrer"
      >
        <img
          alt={iconName}
          style={{
            float: 'left',
            border: 'none',
          }}
          src={socialLink?.iconUrl}
          width={`${EM_SIG_SOCIAL_ICON_SIZES_DICT[size]}`}
          height={`${EM_SIG_SOCIAL_ICON_SIZES_DICT[size]}`}
        />
      </a>
    </td>
  );
};

type CompactContactsRowProps = {
  cells: EmailSignatureContactDetailsField[];
  isLastRow: boolean;
  emailSignatureDesign: EmailSignatureDesign;
  disableAllSpecialEffects: boolean;
  disableAnchorLink: boolean;
  contactDetailsFieldIdWithOutline: EmailSignatureContactDetailsFieldId | null;
};

const CompactContactsRow: FC<React.PropsWithChildren<CompactContactsRowProps>> = ({
  cells,
  isLastRow,
  emailSignatureDesign,
  disableAllSpecialEffects,
  disableAnchorLink,
  contactDetailsFieldIdWithOutline,
}) => {
  const turnOnFieldWidthOutlineForFirstCell = useMemo(() => {
    if (cells.length > 0) {
      return cells[0].id === contactDetailsFieldIdWithOutline;
    } else return false;
  }, [cells, contactDetailsFieldIdWithOutline]);

  const turnOnFieldWidthOutlineForSecondCell = useMemo(() => {
    if (cells.length > 1) {
      return cells[1].id === contactDetailsFieldIdWithOutline;
    } else return false;
  }, [cells, contactDetailsFieldIdWithOutline]);

  const firstCellIconImage = useMemo(() => {
    if (cells.length === 0) return '';

    return cells[0].iconUrl;
  }, [cells]);

  const secondCellIconImage = useMemo(() => {
    if (cells.length < 2) return '';

    return cells[1].iconUrl;
  }, [cells]);

  if (cells.length === 1) {
    return (
      <>
        <tr>
          <td>
            <table style={{ borderCollapse: 'collapse' }} cellSpacing="0" cellPadding="0">
              <tbody>
                <tr>
                  <td
                    role={disableAllSpecialEffects ? undefined : 'button'}
                    tabIndex={disableAllSpecialEffects ? undefined : 0}
                    className={
                      disableAllSpecialEffects
                        ? undefined
                        : styles['interactive-contact-field-one-column']
                    }
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        Emitter.emit(
                          'SCROLL_TO_CONTACT_DETAIL',
                          cells[0].id ? undefined : cells[0].id,
                        );
                      } else if (event.key === ' ') {
                        event.preventDefault();
                      }
                    }}
                    data-field-width-outline={
                      disableAllSpecialEffects ? undefined : turnOnFieldWidthOutlineForFirstCell
                    }
                    onClick={() => {
                      Emitter.emit('SCROLL_TO_CONTACT_DETAIL', cells[0].id);
                    }}
                  >
                    <table
                      style={{
                        borderCollapse: 'collapse',
                        lineHeight: 1,
                      }}
                      cellSpacing="0"
                      cellPadding="0"
                    >
                      <tbody>
                        <tr>
                          {emailSignatureDesign.contactDetailsDisplayIcon && (
                            <td
                              style={{
                                colorScheme: 'only',
                                paddingRight: `${EM_SIG_CONTACT_ICONS_PADDING}px`,
                                width: `${EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE}px`,
                              }}
                            >
                              <img
                                alt={`${cells[0].label || cells[0].type || 'contact'} icon`}
                                style={{
                                  verticalAlign: '-2px',
                                  lineHeight: 1.2,
                                  width: `${EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE}px`,
                                  height: `${EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE}px`,
                                }}
                                width={`${EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE}`}
                                height={`${EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE}`}
                                src={firstCellIconImage}
                              />
                            </td>
                          )}
                          <td
                            style={{
                              maxWidth: `${
                                cells[0].fieldWidth ===
                                EmailSignatureContactDetailsFieldWidth.FULL_WIDTH
                                  ? emailSignatureDesign.contactDetailsDisplayIcon
                                    ? EM_SIG_MAXIMUM_FULL_WIDTH_CONTACT_FIELD_LENGTH +
                                      EM_SIG_CONTACT_CELL_PADDING
                                    : EM_SIG_MAXIMUM_FULL_WIDTH_CONTACT_FIELD_LENGTH_WITHOUT_ICON +
                                      EM_SIG_CONTACT_CELL_PADDING
                                  : emailSignatureDesign.contactDetailsDisplayIcon
                                    ? EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH
                                    : EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH_WITHOUT_ICON
                              }px`,
                              minWidth: `${
                                cells[0].fieldWidth ===
                                EmailSignatureContactDetailsFieldWidth.FULL_WIDTH
                                  ? EM_SIG_MAXIMUM_FULL_WIDTH_CONTACT_FIELD_LENGTH +
                                    EM_SIG_CONTACT_CELL_PADDING
                                  : EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH
                              }px`,
                              wordBreak: 'break-all',
                              overflowWrap: 'anywhere',
                            }}
                            width={
                              cells[0].fieldWidth ===
                              EmailSignatureContactDetailsFieldWidth.FULL_WIDTH
                                ? EM_SIG_MAXIMUM_FULL_WIDTH_CONTACT_FIELD_LENGTH +
                                  EM_SIG_CONTACT_CELL_PADDING
                                : EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH
                            }
                          >
                            <a
                              onClick={(event) => {
                                event.preventDefault();
                              }}
                              style={{
                                colorScheme: 'only',
                                textDecoration: 'unset',
                              }}
                              href={
                                (!disableAnchorLink &&
                                  getActualLinkForContactDetailsField(cells[0])) ||
                                undefined // we have to set undefined here because if it is empty string, when we copy, the clipboard api with replace it with
                                // url of the actual Blinq dashboard website since it assumes empty string refers to relative path
                              }
                              target="_blank"
                              rel="noreferrer"
                            >
                              {!emailSignatureDesign.contactDetailsDisplayIcon && (
                                <span
                                  style={{
                                    lineHeight: 1.2,
                                    color: `${EM_SIG_FONT_COLOR}`,
                                    fontSize: `${EM_SIG_FONT_SIZE_LABEL_WITHOUT_ICON}px`,
                                    fontFamily: emailSignatureDesign.font,
                                    fontWeight: 'bold',
                                    paddingRight: `${EM_SIG_SOCIAL_ICON_PADDING}px`,
                                  }}
                                >
                                  {capitalize(cells[0].label) || capitalize(cells[0].type)}
                                </span>
                              )}
                              <span
                                style={{
                                  lineHeight: 1.2,
                                  color: `${EM_SIG_FONT_COLOR}`,
                                  fontSize: `${EM_SIG_FONT_SIZE_BASE}px`,
                                  fontFamily: emailSignatureDesign.font,
                                }}
                              >
                                {getActualContactDetailValueToDisplay(cells[0])}
                              </span>
                              {cells[0].extension && (
                                <span
                                  style={{
                                    lineHeight: 1.2,
                                    color: `${EM_SIG_SECONDARY_FONT_COLOR}`,
                                    fontSize: `${EM_SIG_FONT_SIZE_EXTENSION}px`,
                                    fontFamily: emailSignatureDesign.font,
                                  }}
                                >
                                  {formatEmailSignaturePhoneExtension(cells[0].extension)}
                                </span>
                              )}
                              {/* p tag does not work nicely with email html because different email clients will display p tags differently
                                                            so the only way to display paragraphs consistently and reliably across different email clients is to use span
                                                            and br tags*/}
                              <br />
                              {emailSignatureDesign.contactDetailsDisplayIcon && (
                                <span
                                  style={{
                                    color: `${EM_SIG_SECONDARY_FONT_COLOR}`,
                                    fontSize: `${EM_SIG_FONT_SIZE_LABEL}px`,
                                    fontFamily: emailSignatureDesign.font,
                                  }}
                                >
                                  {cells[0].label}
                                </span>
                              )}
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
        {/* we use this trick instead of padding or margin because some email clients do not display correctly */}
        {!isLastRow && (
          <tr>
            <td height="15">&nbsp;</td>
          </tr>
        )}
      </>
    );
  } else if (cells.length === 2) {
    return (
      <>
        <tr>
          <td>
            <table style={{ borderCollapse: 'collapse' }} cellSpacing="0" cellPadding="0">
              <tbody>
                <tr>
                  <td
                    tabIndex={disableAllSpecialEffects ? undefined : 0}
                    className={
                      disableAllSpecialEffects ? undefined : styles['interactive-contact-field']
                    }
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        Emitter.emit('SCROLL_TO_CONTACT_DETAIL', cells[0].id);
                      } else if (event.key === ' ') {
                        event.preventDefault();
                      }
                    }}
                    data-field-width-outline={
                      disableAllSpecialEffects ? undefined : turnOnFieldWidthOutlineForFirstCell
                    }
                    onClick={() => {
                      Emitter.emit('SCROLL_TO_CONTACT_DETAIL', cells[0].id);
                    }}
                    style={{
                      paddingRight: `${EM_SIG_CONTACT_CELL_PADDING}px`,
                      maxWidth: `${
                        EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH_WITHOUT_ICON +
                        EM_SIG_CONTACT_CELL_PADDING
                      }px`,
                    }}
                  >
                    <table
                      style={{
                        borderCollapse: 'collapse',
                        lineHeight: 1,
                      }}
                      cellSpacing="0"
                      cellPadding="0"
                    >
                      <tbody>
                        <tr>
                          {emailSignatureDesign.contactDetailsDisplayIcon && (
                            <td
                              style={{
                                colorScheme: 'only',
                                paddingRight: `${EM_SIG_CONTACT_ICONS_PADDING}px`,
                                width: `${EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE}px`,
                              }}
                            >
                              <img
                                alt={`${cells[0].label || cells[0].type || 'contact'} icon`}
                                style={{
                                  verticalAlign: '-2px',
                                  width: `${EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE}px`,
                                  height: `${EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE}px`,
                                }}
                                width={`${EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE}`}
                                height={`${EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE}`}
                                src={firstCellIconImage}
                              />
                            </td>
                          )}
                          <td
                            style={{
                              maxWidth: `${
                                emailSignatureDesign.contactDetailsDisplayIcon
                                  ? EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH
                                  : EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH_WITHOUT_ICON
                              }px`,
                              minWidth: `${
                                emailSignatureDesign.contactDetailsDisplayIcon
                                  ? EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH
                                  : EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH_WITHOUT_ICON
                              }px`,
                              wordBreak: 'break-all',
                              overflowWrap: 'anywhere',
                            }}
                            width={
                              emailSignatureDesign.contactDetailsDisplayIcon
                                ? EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH
                                : EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH_WITHOUT_ICON
                            }
                          >
                            <a
                              onClick={(event) => {
                                event.preventDefault();
                              }}
                              style={{
                                colorScheme: 'only',
                                textDecoration: 'unset',
                              }}
                              href={
                                (!disableAnchorLink &&
                                  getActualLinkForContactDetailsField(cells[0])) ||
                                undefined // we have to set undefined here because if it is empty string, when we copy, the clipboard api with replace it with
                                // url of the actual Blinq dashboard website since it assumes empty string refers to relative path
                              }
                              target="_blank"
                              rel="noreferrer"
                            >
                              {!emailSignatureDesign.contactDetailsDisplayIcon && (
                                <span
                                  style={{
                                    lineHeight: 1.2,
                                    color: `${EM_SIG_FONT_COLOR}`,
                                    fontSize: `${EM_SIG_FONT_SIZE_LABEL_WITHOUT_ICON}px`,
                                    fontFamily: emailSignatureDesign.font,
                                    fontWeight: 'bold',
                                    paddingRight: `${EM_SIG_SOCIAL_ICON_PADDING}px`,
                                  }}
                                >
                                  {capitalize(cells[0].label) || capitalize(cells[0].type)}
                                </span>
                              )}
                              <span
                                style={{
                                  color: `${EM_SIG_FONT_COLOR}`,
                                  fontSize: `${EM_SIG_FONT_SIZE_BASE}px`,
                                  fontFamily: emailSignatureDesign.font,
                                }}
                              >
                                {getActualContactDetailValueToDisplay(cells[0])}
                              </span>
                              {cells[0].extension && (
                                <span
                                  style={{
                                    lineHeight: 1.2,
                                    color: `${EM_SIG_SECONDARY_FONT_COLOR}`,
                                    fontSize: `${EM_SIG_FONT_SIZE_EXTENSION}px`,
                                    fontFamily: emailSignatureDesign.font,
                                  }}
                                >
                                  {formatEmailSignaturePhoneExtension(cells[0].extension)}
                                </span>
                              )}
                              {/* p tag does not work nicely with email html because different email clients will display p tags differently
                                                            so the only way to display paragraphs consistently and reliably across different email clients is to use span
                                                            and br tags*/}
                              <br />
                              {emailSignatureDesign.contactDetailsDisplayIcon && (
                                <span
                                  style={{
                                    color: `${EM_SIG_SECONDARY_FONT_COLOR}`,
                                    fontSize: `${EM_SIG_FONT_SIZE_LABEL}px`,
                                    fontFamily: emailSignatureDesign.font,
                                  }}
                                >
                                  {cells[0].label}
                                </span>
                              )}
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                  <td
                    tabIndex={disableAllSpecialEffects ? undefined : 0}
                    className={
                      disableAllSpecialEffects
                        ? undefined
                        : styles['interactive-contact-field-second-column']
                    }
                    data-field-width-outline={
                      disableAllSpecialEffects ? undefined : turnOnFieldWidthOutlineForSecondCell
                    }
                    data-is-second-row={true}
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        Emitter.emit('SCROLL_TO_CONTACT_DETAIL', cells[1].id);
                      } else if (event.key === ' ') {
                        event.preventDefault();
                      }
                    }}
                    onClick={() => {
                      Emitter.emit('SCROLL_TO_CONTACT_DETAIL', cells[1].id);
                    }}
                    style={{
                      maxWidth: `${
                        EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH_WITHOUT_ICON +
                        EM_SIG_CONTACT_CELL_PADDING
                      }px`,
                    }}
                  >
                    <table
                      style={{
                        borderCollapse: 'collapse',
                        lineHeight: 1,
                      }}
                      cellSpacing="0"
                      cellPadding="0"
                    >
                      <tbody>
                        <tr>
                          {emailSignatureDesign.contactDetailsDisplayIcon && (
                            <td
                              style={{
                                colorScheme: 'only',
                                paddingRight: `${EM_SIG_CONTACT_ICONS_PADDING}px`,
                                width: `${EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE}px`,
                              }}
                            >
                              <img
                                alt={`${cells[1].label || cells[1].type || 'contact'} icon`}
                                style={{
                                  verticalAlign: '-2px',
                                  lineHeight: 1.2,
                                  width: `${EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE}px`,
                                  height: `${EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE}px`,
                                }}
                                width={`${EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE}`}
                                height={`${EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE}`}
                                src={secondCellIconImage}
                              />
                            </td>
                          )}
                          <td
                            style={{
                              maxWidth: `${
                                emailSignatureDesign.contactDetailsDisplayIcon
                                  ? EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH +
                                    EM_SIG_CONTACT_CELL_PADDING
                                  : EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH_WITHOUT_ICON
                              }px`,
                              minWidth: `${
                                emailSignatureDesign.contactDetailsDisplayIcon
                                  ? EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH +
                                    EM_SIG_CONTACT_CELL_PADDING
                                  : EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH_WITHOUT_ICON
                              }px`,
                              wordBreak: 'break-all',
                              overflowWrap: 'anywhere',
                            }}
                            width={
                              emailSignatureDesign.contactDetailsDisplayIcon
                                ? EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH +
                                  EM_SIG_CONTACT_CELL_PADDING
                                : EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH_WITHOUT_ICON
                            }
                          >
                            <a
                              onClick={(event) => {
                                event.preventDefault();
                              }}
                              style={{
                                colorScheme: 'only',
                                textDecoration: 'unset',
                              }}
                              href={
                                (!disableAnchorLink &&
                                  getActualLinkForContactDetailsField(cells[1])) ||
                                undefined // we have to set undefined here because if it is empty string, when we copy, the clipboard api with replace it with
                                // url of the actual Blinq dashboard website since it assumes empty string refers to relative path
                              }
                              target="_blank"
                              rel="noreferrer"
                            >
                              {!emailSignatureDesign.contactDetailsDisplayIcon && (
                                <span
                                  style={{
                                    lineHeight: 1.2,
                                    color: `${EM_SIG_FONT_COLOR}`,
                                    fontSize: `${EM_SIG_FONT_SIZE_LABEL_WITHOUT_ICON}px`,
                                    fontFamily: emailSignatureDesign.font,
                                    fontWeight: 'bold',
                                    paddingRight: `${EM_SIG_SOCIAL_ICON_PADDING}px`,
                                  }}
                                >
                                  {capitalize(cells[1].label) || capitalize(cells[1].type)}
                                </span>
                              )}
                              <span
                                style={{
                                  lineHeight: 1.2,
                                  color: `${EM_SIG_FONT_COLOR}`,
                                  fontSize: `${EM_SIG_FONT_SIZE_BASE}px`,
                                  fontFamily: emailSignatureDesign.font,
                                }}
                              >
                                {getActualContactDetailValueToDisplay(cells[1])}
                              </span>
                              {cells[1].extension && (
                                <span
                                  style={{
                                    lineHeight: 1.2,
                                    color: `${EM_SIG_SECONDARY_FONT_COLOR}`,
                                    fontSize: `${EM_SIG_FONT_SIZE_EXTENSION}px`,
                                    fontFamily: emailSignatureDesign.font,
                                  }}
                                >
                                  {formatEmailSignaturePhoneExtension(cells[1].extension)}
                                </span>
                              )}
                              {/* p tag does not work nicely with email html because different email clients will display p tags differently
                                                            so the only way to display paragraphs consistently and reliably across different email clients is to use span
                                                            and br tags*/}
                              <br />
                              {emailSignatureDesign.contactDetailsDisplayIcon && (
                                <span
                                  style={{
                                    color: `${EM_SIG_SECONDARY_FONT_COLOR}`,
                                    fontSize: `${EM_SIG_FONT_SIZE_LABEL}px`,
                                    fontFamily: emailSignatureDesign.font,
                                  }}
                                >
                                  {cells[1].label}
                                </span>
                              )}
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
        {/* we use this trick instead of padding or margin because some email clients do not display correctly */}
        {!isLastRow && (
          <tr>
            <td height="15">&nbsp;</td>
          </tr>
        )}
      </>
    );
  } else return <></>;
};

const CompactSeparator: FC<
  React.PropsWithChildren<{
    emailSignatureDesign: EmailSignatureDesign;
    isBelowPersonalDetails: boolean;
  }>
> = ({ emailSignatureDesign, isBelowPersonalDetails }) => {
  const calculatedPaddingBottom = useMemo(() => {
    if (isBelowPersonalDetails) {
      return 15;
    } else {
      return 10;
    }
  }, [isBelowPersonalDetails]);

  return (
    <td
      style={{
        lineHeight: '0',
        paddingTop: `${isBelowPersonalDetails ? 8 : 15}px`,
        paddingBottom: `${calculatedPaddingBottom}px`,
      }}
    >
      <table cellSpacing="0" cellPadding="0" style={{ width: '100%' }}>
        <tbody>
          <tr>
            <td
              style={{
                lineHeight: '0',
                fontSize: '1pt',
                borderBottom: `1px solid ${emailSignatureDesign.themeColor}`,
                minWidth: `${MAIN_SECTION_WITHOUT_IMAGE_WIDTH}px`,
                maxWidth: `${MAIN_SECTION_WITHOUT_IMAGE_WIDTH}px`,
              }}
            >
              &nbsp;
            </td>
          </tr>
        </tbody>
      </table>
    </td>
  );
};

export default EmailSignatureCompactMainSection;
